import { Box, Skeleton, Typography } from '@mui/material';
import React from 'react';

import ContextBreadcrumbs from '@sophocles/components/App/ContextBreadcrumbs';
import useBreadcrumbs from '@sophocles/hooks/useBreadcrumbs';

const actionStyles = {
  display: 'flex',
  alignItems: 'center',
  '& .MuiIconButton-root': {
    height: 'calc(var(--title-bar-height) - var(--space-1))',
    width: 'calc(var(--title-bar-height) - var(--space-1))',
    marginLeft: 1,
    backgroundColor: 'var(--title-action-bg)',
    border: 1,
    borderColor: 'var(--title-action-border)',
    boxShadow: 'var(--card-shadow-raised)',
    '&:hover': {
      backgroundColor: 'var(--title-action-hover)',
      boxShadow: 'var(--card-shadow-raised-hover)',
    },
    '&:active': {
      boxShadow: 'var(--card-shadow-raised-active)',
    },
    '&.Mui-disabled': {
      boxShadow: 'none',
    },
  },
  '& .MuiButton-root': {
    height: 'var(--title-bar-height)',
    marginLeft: 1,
  },
};

/**
 * TitleBar - Page header with breadcrumbs navigation
 *
 * When showBackButton is true, auto-generates breadcrumbs from the
 * current route (Customer > Site Group > Site > title).
 */
function TitleBar({ title, subTitle, showBackButton, breadcrumbs: explicitBreadcrumbs, items, actions, loading, sx }) {
  const autoBreadcrumbs = useBreadcrumbs();

  // Build final breadcrumbs: auto crumbs + title as current page
  const breadcrumbs = (() => {
    if (explicitBreadcrumbs) return explicitBreadcrumbs;
    if (autoBreadcrumbs.length === 0) return null;
    const lastCrumb = autoBreadcrumbs[autoBreadcrumbs.length - 1];
    if (title && title !== lastCrumb.label) {
      return [...autoBreadcrumbs, { label: title }];
    }
    // The page IS the last crumb, so it stops linking — but keeps what it
    // says about itself: the level icon, and whether the group is virtual.
    return [...autoBreadcrumbs.slice(0, -1), { ...lastCrumb, to: undefined }];
  })();

  if (breadcrumbs && breadcrumbs.length > 0) {
    return (
      <Box
        sx={{
          display: { xs: 'block', sm: 'flex' },
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          ...sx,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1, display: { xs: 'none', mobile: 'block' } }}>
          {loading ? <Skeleton variant="text" width={200} height={24} /> : <ContextBreadcrumbs crumbs={breadcrumbs} />}
        </Box>
        <Box sx={actionStyles}>
          {loading ? <Skeleton variant="rounded" width={100} height={36} /> : items}
          {loading ? <Skeleton variant="rounded" width={80} height={36} /> : actions}
        </Box>
      </Box>
    );
  }

  // No breadcrumbs — simple title
  return (
    <Box
      sx={{
        display: { xs: 'block', sm: 'flex' },
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 1,
        ...sx,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h5" noWrap sx={{ fontWeight: 600, fontSize: '1.25rem', color: 'text.primary' }}>
          {loading ? <Skeleton variant="text" width={150} /> : title}
        </Typography>
        {subTitle && (
          <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            {loading ? <Skeleton variant="text" width={100} /> : subTitle}
          </Typography>
        )}
      </Box>
      <Box sx={actionStyles}>
        {loading ? <Skeleton variant="rounded" width={100} height={36} /> : items}
        {loading ? <Skeleton variant="rounded" width={80} height={36} /> : actions}
      </Box>
    </Box>
  );
}

export default TitleBar;
